import { Component, inject, Output, EventEmitter, signal, OnInit } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { CurrentUserService } from '../../../services/current-user.service';
import { SCOPES } from '../../../services/scope/scope-constants';
import { HasScopeDirective } from '../../../services/scope/has-scope.directive';
import { ScopeService } from '../../../services/scope/scope-service';
import { InstapayIcon } from '../../../shared/ui/icons/instapay-icon/instapay-icon';
import { DocumentCheckIcon } from '../../../shared/ui/icons/document-check-icon/document-check-icon';
import { ChevronBottomIcon } from '../../../shared/ui/icons/chevron-bottom-icon/chevron-bottom-icon';
import { FileTextIcon } from '../../../shared/ui/icons/file-text-icon/file-text-icon';
import { InvoiceIcon } from '../../../shared/ui/icons/invoice-icon/invoice-icon';
import { BankIcon } from '../../../shared/ui/icons/bank-icon/bank-icon';
import { GearIcon } from '../../../shared/ui/icons/gear-icon/gear-icon';
import { GovernorateIcon } from '../../../shared/ui/icons/governorate-icon/governorate-icon';
import { UserManagementIcon } from '../../../shared/ui/icons/user-management-icon/user-management-icon';
import { DepartmentIcon } from '../../../shared/ui/icons/department-icon/department-icon';
import { DocumentOrderIcon } from '../../../shared/ui/icons/document-order-icon/document-order-icon';

@Component({
  selector: 'app-admin-sidebar',
  imports: [
    RouterLink,
    RouterLinkActive,
    HasScopeDirective,
    DocumentCheckIcon,
    ChevronBottomIcon,
    FileTextIcon,
    InvoiceIcon,
    InstapayIcon,
    BankIcon,
    GearIcon,
    GovernorateIcon,
    DocumentOrderIcon,
    UserManagementIcon,
    DepartmentIcon,
  ],
  templateUrl: './admin-sidebar.html',
  styleUrl: './admin-sidebar.scss',
})
export class AdminSidebar implements OnInit {
  readonly SCOPES = SCOPES;
  private router = inject(Router);
  private currentUserService = inject(CurrentUserService);
  private scopeService = inject(ScopeService);

  isLookupsOpen = signal<boolean>(false);
  isProductsOpen = signal<boolean>(false);
  isPromotionsOpen = signal<boolean>(false);
  isPharmaciesOpen = signal<boolean>(false);
  isInvoicesOpen = signal<boolean>(false);
  isRoutesOpen = signal<boolean>(false);
  isSystemConfigsOpen = signal(false);
  isOrdersOpen = signal(false);
  isUserManagementOpen = signal(false);

  @Output() sidebarToggle = new EventEmitter<void>();

  get showApprovedOnly(): boolean {
    return (
      !this.scopeService.hasAnyScope([SCOPES.PHARMACIES.READ_ALL]) &&
      this.scopeService.hasAnyScope([SCOPES.PHARMACIES.READ_ALL_APPROVED])
    );
  }

  ngOnInit(): void {
    this.router.events.subscribe(() => {
      const isLookupRoute =
        this.router.url.startsWith('/admin/categories') ||
        this.router.url.startsWith('/admin/subcategories') ||
        this.router.url.startsWith('/admin/companies') ||
        this.router.url.startsWith('/admin/suppliers') ||
        this.router.url.startsWith('/admin/undelivered-reasons') ||
        this.router.url.startsWith('/admin/partially-package-reasons') ||
        this.router.url.startsWith('/admin/partially-delivered-reasons') ||
        this.router.url.startsWith('/admin/cancellation') ||
        this.router.url.startsWith('/admin/instapay-Accounts') ||
        this.router.url.startsWith('/admin/bank-account') ||
        this.router.url.startsWith('/admin/rejection-reasons');

      const isProductRoute =
        this.router.url.startsWith('/admin/products') ||
        this.router.url.startsWith('/admin/stock') ||
        this.router.url.startsWith('/admin/attributes');

      const isPromotionRoute =
        this.router.url.startsWith('/admin/offers') || this.router.url.startsWith('/admin/bundles');

      const isPharmacyRoute =
        this.router.url.startsWith('/admin/pharmacies') ||
        this.router.url.startsWith('/admin/approved-pharmacies') ||
        this.router.url.startsWith('/admin/pharmacy-segments');

      const isInvoicesRoute =
        this.router.url.startsWith('/admin/account-statement') ||
        this.router.url.startsWith('/admin/payment-requests') ||
        this.router.url.startsWith('/admin/invoices');

      const isRoutesRoute =
        this.router.url.startsWith('/admin/routes') || this.router.url.startsWith('/admin/route/instance');

      const isOrderRoute =
        this.router.url.startsWith('/admin/orders') || this.router.url.startsWith('/admin/returns-requests');

      const isUserManagementRoute =
        this.router.url.startsWith('/admin/user-management') || this.router.url.startsWith('/admin/departments');

      this.isPromotionsOpen.set(isPromotionRoute);
      this.isLookupsOpen.set(isLookupRoute);
      this.isProductsOpen.set(isProductRoute);
      this.isPharmaciesOpen.set(isPharmacyRoute);
      this.isRoutesOpen.set(isRoutesRoute);
      this.isInvoicesOpen.set(isInvoicesRoute);
      this.isOrdersOpen.set(isOrderRoute);
      this.isUserManagementOpen.set(isUserManagementRoute);
    });
  }

  hideSidebar() {
    this.sidebarToggle.emit();
  }

  isActive(route: string): boolean {
    return this.router.url.startsWith(route);
  }

  logout() {
    this.currentUserService.logout();
    this.router.navigate(['/login'], { replaceUrl: true });
  }
  private readonly lookupRoutes = [
    { path: '/admin/categories', scope: SCOPES.CATEGORIES.READ_ALL },
    { path: '/admin/subcategories', scope: SCOPES.SUB_CATEGORIES.READ_ALL },
    { path: '/admin/companies', scope: SCOPES.COMPANIES.READ_ALL },
    { path: '/admin/suppliers', scope: SCOPES.SUPPLIERS.READ_ALL },
    { path: '/admin/undelivered-reasons', scope: SCOPES.UNDELIVERED_REASONS.READ_ALL },
    { path: '/admin/partially-delivered-reasons', scope: SCOPES.PARTIALLY_DELIVERED_REASONS.READ_ALL },
    { path: '/admin/cancellation', scope: SCOPES.CANCELLATION_REASONS.READ_ALL },
    { path: '/admin/rejection-reasons', scope: SCOPES.REJECTION_REASONS.READ_ALL },
  ];

  goToLookups(event: Event) {
    event.stopPropagation();
    this.isLookupsOpen.set(true);

    const alreadyOnLookups = this.lookupRoutes.some((r) => this.router.url.startsWith(r.path));
    if (alreadyOnLookups) return;

    const target = this.lookupRoutes.find((r) => this.scopeService.hasAnyScope([r.scope]));
    if (target) {
      this.router.navigate([target.path]);
    }
  }

  goToProducts(event: Event) {
    event.stopPropagation();
    this.isProductsOpen.set(true);
    if (!this.router.url.startsWith('/admin/products')) {
      this.router.navigate(['/admin/products']);
    }
  }

  goToPharmacies(event: Event) {
    event.stopPropagation();
    this.isPharmaciesOpen.update((val) => !val);

    const alreadyOnPharmacies =
      this.router.url.startsWith('/admin/pharmacies') ||
      this.router.url.startsWith('/admin/approved-pharmacies') ||
      this.router.url.startsWith('/admin/pharmacy-segments');

    if (!alreadyOnPharmacies) {
      if (this.scopeService.hasAnyScope([SCOPES.PHARMACIES.READ_ALL])) {
        this.router.navigate(['/admin/pharmacies']);
      } else if (this.scopeService.hasAnyScope([SCOPES.PHARMACIES.READ_ALL_APPROVED])) {
        this.router.navigate(['/admin/approved-pharmacies']);
      }
    }
  }

  goToUserManagement(event: Event) {
    event.stopPropagation();
    this.isUserManagementOpen.update((val) => !val);

    const alreadyOnUserManagement =
      this.router.url.startsWith('/admin/user-management') || this.router.url.startsWith('/admin/departments');

    if (!alreadyOnUserManagement) {
      this.router.navigate(['/admin/user-management']);
    }
  }

  goToPromotions(event: Event) {
    event.stopPropagation();

    this.isPromotionsOpen.update((val) => !val);

    const alreadyOnPromotions =
      this.router.url.startsWith('/admin/offers') || this.router.url.startsWith('/admin/bundles');

    if (!alreadyOnPromotions) {
      if (this.scopeService.hasAnyScope([SCOPES.OFFERS.READ_ALL])) {
        this.router.navigate(['/admin/offers']);
      } else if (this.scopeService.hasAnyScope([SCOPES.BUNDLES.READ_ALL])) {
        this.router.navigate(['/admin/bundles']);
      }
    }
  }

  goToRoutes(event: Event) {
    event.stopPropagation();
    this.isRoutesOpen.update((v) => !v);
    if (!this.router.url.startsWith('/admin/routes') && !this.router.url.startsWith('/admin/route-instances')) {
      if (this.scopeService.hasAnyScope([SCOPES.ROUTES.READ_ALL])) this.router.navigate(['/admin/routes']);
      else if (this.scopeService.hasAnyScope([SCOPES.ROUTES_INSTANCE.READ_ALL]))
        this.router.navigate(['/admin/route-instances']);
    }
  }

  gotoOrders(event: Event) {
    event.stopPropagation();
    this.isOrdersOpen.update((v) => !v);
    if (!this.router.url.startsWith('/admin/orders') && !this.router.url.startsWith('/admin/returns-requests')) {
      if (this.scopeService.hasAnyScope([SCOPES.ORDERS.READ_ALL])) {
        this.router.navigate(['/admin/orders']);
      }
    }
  }

  openSystemConfigs(event: Event) {
    event.stopPropagation();
    this.isSystemConfigsOpen.update((v) => !v);
    if (!this.router.url.startsWith('/admin/governorates')) {
      if (this.scopeService.hasAnyScope([SCOPES.ROUTES.READ_ALL])) this.router.navigate(['/admin/governorates']);
    }
  }

  goToInvoices(event: Event) {
    event.stopPropagation();
    this.isInvoicesOpen.update((v) => !v);
    if (
      !this.router.url.startsWith('/admin/invoices') &&
      !this.router.url.startsWith('/admin/account-statement') &&
      !this.router.url.startsWith('/admin/payment-requests')
    ) {
      // if (this.scopeService.hasAnyScope([SCOPES.INVOICES.READ_ALL])) this.router.navigate(['/admin/invoices']);
      // else if (this.scopeService.hasAnyScope([SCOPES.ACCOUNT_STATEMENT.READ_ALL]))
      this.router.navigate(['/admin/invoices']);
    }
  }
}
